/**
 * React Query hooks for Location API operations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "@/api/client";
import type { Location, CreateLocationInput, UpdateLocationInput } from "@/api/types";

const LOCATIONS_KEY = ["locations"];

/**
 * Fetch all locations.
 */
export function useLocations() {
  return useQuery({
    queryKey: LOCATIONS_KEY,
    queryFn: () => api.get<Location[]>("/locations"),
  });
}

/**
 * Fetch a single location by ID.
 */
export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: [...LOCATIONS_KEY, id],
    queryFn: () => api.get<Location>(`/locations/${id as string}`),
    enabled: !!id,
  });
}

/**
 * Create a new location.
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLocationInput) => api.post<Location>("/locations", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATIONS_KEY });
    },
  });
}

/**
 * Update an existing location.
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateLocationInput & { id: string }) =>
      api.patch<Location>(`/locations/${id}`, input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: LOCATIONS_KEY });
      queryClient.setQueryData([...LOCATIONS_KEY, data.id], data);
    },
  });
}

/**
 * Delete a location.
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.del(`/locations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATIONS_KEY });
    },
  });
}
